/**
 * The colour each piece's swatch shows in the Look panel (ADR-094 decision 24's swatches). The looks table, the
 * catalogue and the look sheets' pointer hold no colour, and the panel fetches only the sheets of the pieces shown
 * (decision 27), so a swatch cannot be read from its piece's sheet on the page. Each entry is instead measured once
 * from that sheet: the commonest colour of the piece's standing cell, facing front, other than the packs' outline.
 * `tests/looks-swatches.test.ts` holds every entry to that measure wherever the private look fixtures are, and fails
 * for a piece a player may choose that has art and no entry, so a piece appended to the table gains its line here.
 */

/**
 * The packs' outline colour, which every piece's sheet holds and no swatch shows.
 */
export const LOOK_OUTLINE_COLOUR = "#181818";

/**
 * The swatch colour of each piece a player may choose, by piece key.
 */
export const LOOK_SWATCH_COLOURS: Readonly<Record<string, string>> = Object.freeze({
  "humn-1": "#f8d098",
  "humn-2": "#f8c0b0",
  "humn-3": "#ffc784",
  "humn-4": "#f0c080",
  "humn-5": "#f7a273",
  "humn-6": "#e09060",
  "humn-7": "#bc6633",
  "humn-8": "#d5aa72",
  "humn-9": "#dea05b",
  "humn-10": "#b28246",
  "bob1-1": "#7c8989",
  "bob1-2": "#786850",
  "bob1-3": "#a67222",
  "bob1-4": "#8a4f57",
  "bob1-5": "#943818",
  "bob1-6": "#ac5580",
  "bob1-7": "#4e4878",
  "bob1-8": "#56747e",
  "bob1-9": "#2d5191",
  "bob1-10": "#4d4d5a",
  "bob1-11": "#4e2b20",
  "bob1-12": "#ae4593",
  "bob1-13": "#453f60",
  "dap1-1": "#7c8989",
  "dap1-2": "#786850",
  "dap1-3": "#a67222",
  "dap1-4": "#8a4f57",
  "dap1-5": "#943818",
  "dap1-6": "#ac5580",
  "dap1-7": "#4e4878",
  "dap1-8": "#56747e",
  "dap1-9": "#2d5191",
  "dap1-10": "#4d4d5a",
  "dap1-11": "#4e2b20",
  "dap1-12": "#ae4593",
  "dap1-13": "#453f60",
  "fstr-1": "#a69878",
  "fstr-2": "#b08898",
  "fstr-3": "#7c8989",
  "fstr-4": "#cd9217",
  "fstr-5": "#389878",
  "pfpn-1": "#3a6797",
  "pfpn-2": "#986038",
  "pfpn-3": "#586870",
  "pfpn-4": "#389088",
  "pfpn-5": "#8aafac",
});

/**
 * The colour a piece's swatch shows.
 *
 * @param key The piece's key.
 * @returns The colour as `#rrggbb`, or null for a piece with no entry, whose swatch shows its colour number on the
 *   kit's surface instead.
 * @example
 * swatchColour("humn-3"); // "#ffc784"
 */
export function swatchColour(key: string): string | null {
  return Object.hasOwn(LOOK_SWATCH_COLOURS, key) ? (LOOK_SWATCH_COLOURS[key] as string) : null;
}
